// Per-hosting data that the API only exposes per hosting id (backups, resource
// usage). There is no customer-wide endpoint, so this fans out one request per
// hosting; a failed request leaves that hosting out of the result.
import { useEffect, useMemo, useState } from 'react';
import { get } from './api.js';
import { localeFor, parseApiDate } from './format.js';

/** @returns {Record<number, any> | null} null while loading */
export function useHostingFanOut(hostings, pathFor) {
  const [results, setResults] = useState(null);
  const ids = (hostings || []).map((h) => h.id).join(',');

  useEffect(() => {
    if (!hostings) return undefined;
    let cancelled = false;
    Promise.allSettled(hostings.map((h) => get(pathFor(h.id)))).then((settled) => {
      if (cancelled) return;
      const out = {};
      settled.forEach((r, i) => {
        if (r.status === 'fulfilled') out[hostings[i].id] = r.value;
      });
      setResults(out);
    });
    return () => {
      cancelled = true;
    };
    // Keyed on the id list so a re-render with the same hostings doesn't refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids, !!hostings]);

  return results;
}

export function summarizeBackups(list) {
  const backups = Array.isArray(list) ? list : [];
  const completed = backups.filter((b) => b.status === 'completed');
  const finished = backups.filter((b) => b.status === 'completed' || b.status === 'failed');
  const time = (b) => parseApiDate(b.completed_at || b.created_at)?.getTime() || 0;
  const latest = [...backups].sort((a, b) => time(b) - time(a))[0] || null;
  const lastCompleted = [...completed].sort((a, b) => time(b) - time(a))[0] || null;
  return {
    count: completed.length,
    totalBytes: completed.reduce((a, b) => a + (b.size_bytes || 0), 0),
    lastCompletedAt: lastCompleted ? lastCompleted.completed_at || lastCompleted.created_at : null,
    latestStatus: latest?.status || null,
    successRate: finished.length ? completed.length / finished.length : null,
  };
}

/** @returns {Record<number, ReturnType<typeof summarizeBackups>> | null} */
export function useBackupSummaries(hostings) {
  const raw = useHostingFanOut(hostings, (id) => `/backups/hosting/${id}`);
  return useMemo(() => {
    if (!raw) return null;
    const out = {};
    for (const [id, list] of Object.entries(raw)) out[id] = summarizeBackups(list);
    return out;
  }, [raw]);
}

/** Latest resource sample per hosting (404 = never sampled → absent). */
export function useResourceUsage(hostings) {
  return useHostingFanOut(hostings, (id) => `/resources/hosting/${id}/current`);
}

export function formatBytes(bytes, lang = 'nl') {
  const n = Number(bytes) || 0;
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${new Intl.NumberFormat(localeFor(lang), { maximumFractionDigits: v < 10 && i > 0 ? 1 : 0 }).format(v)} ${units[i]}`;
}
